import {
  allDsoEnum,
  AllDsoEnum,
  DirectionEnum,
  FinlandPlanningMaskModeEnum,
  FrancePlanningMaskModeEnum,
  GroupModeEnum,
  PacketTypeEnum,
  PlanningModeEnum,
  RteCommunicationMarketStatusEnum,
  RteCommunicationMarketTypeEnum,
  SiteModeEnum,
  SiteOperatorEnum,
  TITypeEnum,
} from "shared/enums/globalEnums";
import { MaskModeType } from "shared/types";

export const planningModeColorMapping: Record<PlanningModeEnum, string> = {
  [PlanningModeEnum.soc_management]: "#FAD2E1", // Pink Lavender
  [PlanningModeEnum.fcr_rte_v2]: "#FFDAC1", // Pale Pink
  [PlanningModeEnum.fcr_rte_v2_no_reserve]: "#E2F0CB", // Tea Green
  [PlanningModeEnum.afrr_rte]: "#B5EAD7", // Magic Mint
  [PlanningModeEnum.afrr_fcr_rte]: "#83C5BE", // Middle Blue Green
  [PlanningModeEnum.power_chronicle]: "#FF9AA2", // Melon
  [PlanningModeEnum.power]: "#B28DFF", // Light Purple
  [PlanningModeEnum.stop]: "#FFD166", // Saffron
  [PlanningModeEnum.fingrid_fcrn]: "#006D77", // Cadet Blue
  [PlanningModeEnum.fcr_rte_v2_certification]: "#E07A5F", // Terra Cotta
  [PlanningModeEnum.fingrid_fcrd]: "#43AA8B", // Medium Jade
  [PlanningModeEnum.fingrid_afrr]: "#F49F0A", // Orange Yellow Crayola
};

export const maskModeColorMapping: Record<MaskModeType, string> = {
  // France modes
  [FrancePlanningMaskModeEnum.r2_asymmetrical]: "#EDF6F9", // Cultured (light pastel blue)
  [FrancePlanningMaskModeEnum.r2_chronicles]: "#FFDDD2", // Unbleached Silk (pastel peach)
  [FrancePlanningMaskModeEnum.r1_chronicles]: "#FFD8C2", // Pastel Burnt Sienna
  [FrancePlanningMaskModeEnum.enspired]: "#FFCDD2", // Pastel Rose
  [FrancePlanningMaskModeEnum.r1_r2_mask]: "#A8C8FF", // Pastel Royal Blue
  [FrancePlanningMaskModeEnum.technical_chronicle]: "#A0D2FF", // Pastel Star Command Blue
  [FrancePlanningMaskModeEnum.arbitrage_spot]: "#FFAB91", // Pastel Ruby Red
  [FrancePlanningMaskModeEnum.fake_box_staging]: "#FFE0B2", // Pastel Peach
  [FrancePlanningMaskModeEnum.standby_france]: "#A8DADC", // Pastel Blue-Green
  [FrancePlanningMaskModeEnum.r1_r2_target_soc_band_tests]: "#EDF6F9", // Cultured (light pastel blue)
  [FrancePlanningMaskModeEnum.r2_asymmetrical_test]: "#B5EAD7", // Pastel Mint Green
  [FrancePlanningMaskModeEnum.fcr_standalone]: "#FFD6EC", // Pastel Light Pink (unique)
  [FrancePlanningMaskModeEnum.r2_standalone]: "#A0D8B3", // Pastel Light Green (unique)
  [FrancePlanningMaskModeEnum.r2_tests]: "#E6D6FF", // Pastel Lavender (unique)
  [FrancePlanningMaskModeEnum.france_intraday_standalone]: "#FFE6B8", // Pastel Apricot (unique)
  [FrancePlanningMaskModeEnum.france_intraday_standalone_multisite]: "#B4EDFF", // Pastel Sky Blue (unique)
  [FrancePlanningMaskModeEnum.external]: "#D3D3D3", // Pastel Gray (neutral/external)
  [FrancePlanningMaskModeEnum.r2_capa_asymmetrical]: "#C9E4CA", // Pastel Sage Green (r2_asymmetrical capacity variant)

  // Finland modes
  [FinlandPlanningMaskModeEnum.fcrn_standalone]: "#A7D7C5", // Pastel Teal
  [FinlandPlanningMaskModeEnum.fcrd_standalone]: "#C1B8D2", // Pastel Dark Slate
  [FinlandPlanningMaskModeEnum.fcrd_fcrn_combined]: "#FFF4C2", // Pastel Yellow
  [FinlandPlanningMaskModeEnum.r2_finland]: "#FFCCBC", // Pastel Red-Orange
  [FinlandPlanningMaskModeEnum.finland_r2_asymmetrical]: "#FF9AA2", // Melon
  [FinlandPlanningMaskModeEnum.standby_finland]: "#8ECAE6", // Light Blue (pastel)
  [FinlandPlanningMaskModeEnum.multi_market_fcr_finland]: "#B2F7EF", // Magic Mint
  [FinlandPlanningMaskModeEnum.finland_intraday_standalone]: "#FFD6A5", // Light Apricot
  [FinlandPlanningMaskModeEnum.finland_intraday_standalone_multisite]: "#B6FFB3", // Pale Green
  [FinlandPlanningMaskModeEnum.finland_mFRR_asymmetrical]: "#FFB4A2", // Pastel Salmon (asymmetrical family)
  [FinlandPlanningMaskModeEnum.finland_afrr_intraday]: "#BDE0FE", // Pastel Sky Blue
  [FinlandPlanningMaskModeEnum.finland_afrr_intraday_dp]: "#A2D2FF", // Pastel Cornflower Blue
  [FinlandPlanningMaskModeEnum.finland_afrr_intraday_dp_qh_ph]: "#9BF6FF", // Pastel Cyan
  [FinlandPlanningMaskModeEnum.finland_mfrr_intraday]: "#CDB4DB", // Pastel Lilac
  [FinlandPlanningMaskModeEnum.finland_afrr_mfrr_intraday]: "#FFC8DD", // Pastel Pink (afrr+mfrr combined)
  [FinlandPlanningMaskModeEnum.sweden_intraday_standalone]: "#FEC89A", // Pastel Orange
};

export const dsoColorMapping: Record<AllDsoEnum, string> = {
  [allDsoEnum.caruna]: "#d98880", // Light blue
  [allDsoEnum.enedis]: "#7fb3d5", // Light blue
  [allDsoEnum.geredis]: "#c39bd3", // Light lavender
  [allDsoEnum.elenia]: "#f7dc6f", // Pastel yellow
  [allDsoEnum.savon_voima]: "#76d7c4", // Soft turquoise
  [allDsoEnum.jse]: "#aed6f1", // Pale sky blue
  [allDsoEnum.kymenlaakson]: "#27ae60", // Vivid green
  [allDsoEnum.pks]: "#d35400", // Warm deep orange
  [allDsoEnum.leppakosken_sahko_oy]: "#45aaf2", // Pastel blue
  [allDsoEnum.bitche]: "#f8c291", // Light peach
  [allDsoEnum.alajarven]: "#ffadad", // Pastel red
  [allDsoEnum.enontekion]: "#ffd6a5", // Pastel orange
  [allDsoEnum.esse_elektro_kraft]: "#fdffb6", // Pastel yellow
  [allDsoEnum.haukiputaan]: "#caffbf", // Pastel green
  [allDsoEnum.herrfors]: "#9bf6ff", // Pastel cyan
  [allDsoEnum.imatran]: "#a0c4ff", // Pastel blue
  [allDsoEnum.kajave]: "#bdb2ff", // Pastel indigo
  [allDsoEnum.ksat]: "#ffc6ff", // Pastel violet
  [allDsoEnum.kss]: "#ffb3c1", // Pastel pink
  [allDsoEnum.kuoreveden]: "#b5ead7", // Soft mint
  [allDsoEnum.lahti]: "#c7ceea", // Pastel periwinkle
  [allDsoEnum.lammaisten]: "#ffdac1", // Pastel apricot
  [allDsoEnum.lreoy]: "#e2f0cb", // Pastel lime
  [allDsoEnum.muonion]: "#b5d8ff", // Pastel sky
  [allDsoEnum.nivos]: "#f1c0e8", // Pastel magenta
  [allDsoEnum.okun]: "#a3c4f3", // Pastel cornflower
  [allDsoEnum.oulun_energia]: "#90dbf4", // Pastel azure
  [allDsoEnum.paneliankosken]: "#98f5e1", // Pastel aquamarine
  [allDsoEnum.parikkalan]: "#b9fbc0", // Pastel spring green
  [allDsoEnum.porvoon]: "#fde4cf", // Pale peach
  [allDsoEnum.rovakaira]: "#ffcfd2", // Pale rose
  [allDsoEnum.sallila]: "#d0f4de", // Pale mint
  [allDsoEnum.tls]: "#ffafcc", // Pastel bubblegum
  [allDsoEnum.vaasan]: "#cddafd", // Pastel lavender blue
  [allDsoEnum.vakka_suomen]: "#f9c74f", // Warm yellow
  [allDsoEnum.valkeakosken]: "#f9844a", // Warm orange
  [allDsoEnum.vatajankoski]: "#90be6d", // Warm green
  [allDsoEnum.verkko]: "#f8961e", // Warm amber
  [allDsoEnum.vetelin]: "#43aa8b", // Deep teal
};

export const siteModeColorMapping: Record<SiteModeEnum, string> = {
  [SiteModeEnum.test]: "#FFCDD2", // Pastel Red
  [SiteModeEnum.factory_trials]: "#B2EBF2", // Pastel Cyan
  [SiteModeEnum.operational_trials]: "#C8E6C9", // Pastel Green
  [SiteModeEnum.tso_trials]: "#FFE0B2", // Pastel Peach
  [SiteModeEnum.production]: "#BBDEFB", // Pastel Blue
};

export const TITypeColorMapping: Record<TITypeEnum, string> = {
  [TITypeEnum.P_PROD]: "#BBDEFB", // Pastel Blue
  [TITypeEnum.PART_FSM]: "#FFE0B2", // Pastel Peach
  [TITypeEnum.F_PROD]: "#C8E6C9", // Pastel Green
  [TITypeEnum.PC]: "#D1C4E9", // Pastel Lavender
  [TITypeEnum.SOC_EDR]: "#FFF9C4", // Pastel Light Yellow
  [TITypeEnum.RSFP]: "#B2EBF2", // Pastel Cyan
  [TITypeEnum.N_RSFP]: "#FFCDD2", // Pastel Pink
  [TITypeEnum.RET_RSFP]: "#E1BEE7", // Pastel Mauve
};

export const PacketTypeColorMapping: Record<PacketTypeEnum, string> = {
  [PacketTypeEnum.TM]: "#BBDEFB", // Pastel Blue
  [PacketTypeEnum.TS]: "#C8E6C9", // Pastel Green
  [PacketTypeEnum.TVC]: "#FFE0B2", // Pastel Peach
};

export const RteCommunicationMarketTypeColorMapping: Record<RteCommunicationMarketTypeEnum, string> = {
  [RteCommunicationMarketTypeEnum.fcr]: "#B3E5FC", // Pastel Light Blue
  [RteCommunicationMarketTypeEnum.afrr]: "#FFECB3", // Pastel Amber
};

export const RteCommunicationMarketStatusColorMapping: Record<RteCommunicationMarketStatusEnum, string> = {
  [RteCommunicationMarketStatusEnum.enabled]: "#C8E6C9", // Pastel Green for enabled
  [RteCommunicationMarketStatusEnum.disabled]: "#FFCDD2", // Pastel Red for disabled
};

export const GroupeModeColorMapping: Record<GroupModeEnum, string> = {
  [GroupModeEnum.prod]: "info.main",
  [GroupModeEnum.test]: "warning.main",
};

export const DirectionColorMapping: Record<DirectionEnum, string> = {
  [DirectionEnum.UPWARD]: "#C8E6C9", // Pastel Green
  [DirectionEnum.DOWNWARD]: "#FFCDD2", // Pastel Red
};

export const siteOperatorColorMapping: Record<SiteOperatorEnum, string> = {
  [SiteOperatorEnum.NW_JMET]: "#BBDEFB", // Pastel light blue
  [SiteOperatorEnum.NW_JMET2]: "#C8E6C9", // Pastel light green
  [SiteOperatorEnum.NW_JMET3]: "#FFF9C4", // Pastel light yellow
  [SiteOperatorEnum.NW_JMET4]: "#FFE0B2", // Pastel peach
  [SiteOperatorEnum.NWJ_FIN_Oy]: "#D1C4E9", // Pastel lavender
  [SiteOperatorEnum.NWJ_FIN_2_Oy]: "#FFCDD2", // Pastel red
  [SiteOperatorEnum.NWJ_SWE_AB]: "#B2EBF2", // Pastel cyan (unique)
};
